import { BaseService } from "./BaseService";
import {
  CreateHotelServiceRequest,
  HotelService,
  HotelServiceProviderType,
  HotelServicesFilters,
  HotelServicesResponse,
  HotelServiceStatus,
  HotelServiceImportResult,
  HotelPartnerOrdersSummary,
  PartnerServiceFilterOptions,
  TopServiceByBookings,
  TopServiceByRevenue,
  UpdateHotelServiceRequest,
} from "@/types/hotelService";
import { IHotelServiceService } from "./interfaces/IHotelServiceService";

class HotelServiceService extends BaseService implements IHotelServiceService {
  private getBasePath = (hotelId: string) => `/hotels/${hotelId}/services`;

  constructor() {
    super();
  }
  getAllServices(
    hotelId: string,
    providerTypes: HotelServiceProviderType[] = [
      HotelServiceProviderType.EXTERNAL_SERVICE,
      HotelServiceProviderType.GUESTERIA_PARTNERS_SERVICE,
      HotelServiceProviderType.GUEST_SERVICE,
    ],
  ): Promise<HotelServicesResponse> {
    return this.get<HotelServicesResponse>(this.getBasePath(hotelId), {
      status: HotelServiceStatus.ACTIVE,
      providerType: providerTypes,
    });
  }

  async getServices(
    hotelId: string,
    page?: number,
    limit?: number,
    filters?: HotelServicesFilters,
  ): Promise<HotelServicesResponse> {
    const params: any = { ...filters };
    if (page) params.page = page;
    if (limit) params.limit = limit;

    return this.get<HotelServicesResponse>(this.getBasePath(hotelId), params);
  }

  getFeaturedServices(hotelId: string): Promise<HotelService[]> {
    return this.get<HotelService[]>(`/hotels/${hotelId}/featured-services`);
  }

  getServiceById(id: string, hotelId: string): Promise<HotelService> {
    return this.get<HotelService>(`${this.getBasePath(hotelId)}/${id}`);
  }

  createService(requestData: CreateHotelServiceRequest): Promise<HotelService> {
    const { hotelId, ...data } = requestData;

    return this.post<HotelService>(this.getBasePath(hotelId), data);
  }

  updateService(
    id: string,
    hotelId: string,
    requestData: UpdateHotelServiceRequest,
  ): Promise<HotelService> {
    return this.put<HotelService>(
      `${this.getBasePath(hotelId)}/${id}`,
      requestData,
    );
  }

  deleteService(id: string, hotelId: string): Promise<void> {
    return this.delete<void>(`${this.getBasePath(hotelId)}/${id}`);
  }

  activateService(id: string, hotelId: string): Promise<void> {
    return this.patch<void>(`${this.getBasePath(hotelId)}/${id}/enable`);
  }

  deactivateService(id: string, hotelId: string): Promise<void> {
    return this.patch<void>(`${this.getBasePath(hotelId)}/${id}/disable`);
  }

  setServiceFeatured(
    id: string,
    hotelId: string,
    isRecommended: boolean,
  ): Promise<void> {
    return this.patch<void>(`${this.getBasePath(hotelId)}/${id}/featured`, {
      featured: isRecommended,
    });
  }

  getCheckinServices(hotelId: string): Promise<HotelService[]> {
    return this.get<HotelService[]>(`/hotels/${hotelId}/checkin-services`);
  }

  getCheckoutServices(hotelId: string): Promise<HotelService[]> {
    return this.get<HotelService[]>(`/hotels/${hotelId}/checkout-services`);
  }

  getTopRevenuePartnerOrders(
    hotelId: string,
    from?: string,
    to?: string,
  ): Promise<TopServiceByRevenue | null> {
    const now = new Date();
    const defaultFrom = new Date(
      now.getFullYear(),
      now.getMonth(),
      1,
    ).toISOString();
    const defaultTo = now.toISOString();

    return this.get<TopServiceByRevenue | null>(
      `/hotels/${hotelId}/partners/orders/top-revenue`,
      {
        from: from ?? defaultFrom,
        to: to ?? defaultTo,
      },
    );
  }

  getPartnersOrdersSummary(
    hotelId: string,
    from?: string,
    to?: string,
  ): Promise<HotelPartnerOrdersSummary> {
    const now = new Date();
    const defaultFrom = new Date(
      now.getFullYear(),
      now.getMonth(),
      1,
    ).toISOString();
    const defaultTo = now.toISOString();

    return this.get<HotelPartnerOrdersSummary>(
      `/hotels/${hotelId}/partners/orders/summary`,
      {
        from: from ?? defaultFrom,
        to: to ?? defaultTo,
      },
    );
  }

  getPartnerServices(
    hotelId: string,
    page = 1,
    limit = 10,
  ): Promise<HotelServicesResponse> {
    return this.get<HotelServicesResponse>(
      `/hotels/${hotelId}/services/partners`,
      { page, limit },
    );
  }

  getPartnerServiceById(
    hotelId: string,
    serviceId: string,
  ): Promise<HotelService> {
    return this.get<HotelService>(
      `/hotels/${hotelId}/services/partners/${serviceId}`,
    );
  }

  getPartnerServiceFilterOptions(
    hotelId: string,
  ): Promise<PartnerServiceFilterOptions> {
    return this.get<PartnerServiceFilterOptions>(
      `/hotels/${hotelId}/partners/services/filter-options`,
    );
  }

  exportServices(
    hotelId: string,
    providerType: HotelServiceProviderType,
    format: "csv" | "xlsx" = "csv",
  ): Promise<Blob> {
    return this.getBlob(`${this.getBasePath(hotelId)}/export`, {
      providerType,
      format,
    });
  }

  importServices(
    hotelId: string,
    file: File,
  ): Promise<HotelServiceImportResult> {
    return this.uploadFile<HotelServiceImportResult>(
      `${this.getBasePath(hotelId)}/import`,
      file,
    );
  }

  getTopBookedPartnerOrders(
    hotelId: string,
    from?: string,
    to?: string,
  ): Promise<TopServiceByBookings | null> {
    const now = new Date();
    const defaultFrom = new Date(
      now.getFullYear(),
      now.getMonth(),
      1,
    ).toISOString();
    const defaultTo = now.toISOString();

    return this.get<TopServiceByBookings | null>(
      `/hotels/${hotelId}/partners/orders/top-booked`,
      {
        from: from ?? defaultFrom,
        to: to ?? defaultTo,
      },
    );
  }
}

export const hotelService = new HotelServiceService();
